import React, { useMemo } from 'react';
import { Button, Col, Empty, Modal, Row, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PrinterOutlined } from '@ant-design/icons';
import { BulletinRow, BulletinStatsRow, MatiereBulletin } from './bulletins.types';
import { coefficientBulletinRows, format2, KORI_ORANGE } from './bulletins.utils';

const { Title, Text } = Typography;

type GroupedMatiereBulletin = {
  matiereId: string;
  matiereNom: string;
  rows: MatiereBulletin[];
};

interface BulletinPreviewModalProps {
  visible: boolean;
  selectedBulletin: BulletinRow | null;
  bulletinsCount: number;
  isPeriodeClosed: boolean;
  isSecondaryBulletin: boolean;
  onClose: () => void;
  onGeneratePdf: (bulletin: BulletinRow) => void;
}

export const BulletinPreviewModal = ({
  visible,
  selectedBulletin,
  bulletinsCount,
  isPeriodeClosed,
  isSecondaryBulletin,
  onClose,
  onGeneratePdf,
}: BulletinPreviewModalProps) => {
  const noteScale = selectedBulletin?.noteScale || 20;
  const coefficientMode = noteScale === 20;
  const coefficientRows = useMemo(() => coefficientBulletinRows(selectedBulletin?.matieres || []), [selectedBulletin?.matieres]);
  const groupedMatieres = useMemo<GroupedMatiereBulletin[]>(() => {
    const groups: GroupedMatiereBulletin[] = [];
    (selectedBulletin?.matieres || []).forEach(row => {
      const previous = groups[groups.length - 1];
      if (previous?.matiereId === row.matiereId) {
        previous.rows.push(row);
        return;
      }
      groups.push({ matiereId: row.matiereId, matiereNom: row.matiereNom, rows: [row] });
    });
    return groups;
  }, [selectedBulletin?.matieres]);

  const releveColumns: ColumnsType<GroupedMatiereBulletin> = useMemo(
    () => [
      {
        title: 'Matière',
        dataIndex: 'matiereNom',
        key: 'matiereNom',
        align: 'left',
        render(value: string | null, record) {
          const hasSousMatieres = record.rows.some(row => row.sousMatiereNom && row.sousMatiereNom !== '-');
          if (!hasSousMatieres) {
            return <Text strong>{value || '-'}</Text>;
          }

          return (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: 24 }}>
              <div style={{ paddingRight: 8, borderRight: '1px solid #f0f0f0', fontWeight: 600 }}>{value || '-'}</div>
              <div style={{ paddingLeft: 8 }}>
                {record.rows.map((row, index) => (
                  <div key={`${row.sousMatiereId || 'matiere'}-${index}`} style={{ minHeight: 24 }}>
                    {row.sousMatiereNom || '-'}
                  </div>
                ))}
              </div>
            </div>
          );
        },
      },
      {
        title: 'Note',
        key: 'note',
        align: 'center',
        render(_, record) {
          return record.rows.map((row, index) => (
            <div key={index} style={{ minHeight: 24 }}>
              {format2(row.note)}
            </div>
          ));
        },
      },
      {
        title: 'Note Sur',
        key: 'noteSur',
        align: 'center',
        render(_, record) {
          return record.rows.map((row, index) => (
            <div key={index} style={{ minHeight: 24 }}>
              {format2(row.noteSur)}
            </div>
          ));
        },
      },
      {
        title: 'Appréciation',
        key: 'appreciation',
        align: 'left',
        render(_, record) {
          return record.rows.map((row, index) => (
            <div key={index} style={{ minHeight: 24 }}>
              {row.appreciation || ''}
            </div>
          ));
        },
      },
    ],
    []
  );

  const secondaryColumns: ColumnsType<MatiereBulletin> = useMemo(
    () => [
      {
        title: 'Matières',
        dataIndex: 'matiereNom',
        key: 'matiereNom',
        align: 'left',
        render(value: string | null) {
          return <Text strong>{value || '-'}</Text>;
        },
      },
      {
        title: 'C.C',
        dataIndex: 'moyenneDevoirs',
        key: 'moyenneDevoirs',
        align: 'center',
        render: format2,
      },
      {
        title: 'Comp.',
        dataIndex: 'moyenneComposition',
        key: 'moyenneComposition',
        align: 'center',
        render: format2,
      },
      {
        title: 'Moy./20',
        dataIndex: 'moyenneSur20',
        key: 'moyenneSur20',
        align: 'center',
        render(value: number | null) {
          const color = value === null || value === undefined ? '#64748b' : value >= 10 ? '#15803d' : '#b91c1c';
          return <Text style={{ color, fontWeight: 700 }}>{format2(value)}</Text>;
        },
      },
      {
        title: 'Coef.',
        dataIndex: 'coefficient',
        key: 'coefficient',
        align: 'center',
        render: (value: number | null) => value ?? '-',
      },
      {
        title: 'Moy.X',
        dataIndex: 'moyenneApresCoefficient',
        key: 'moyenneApresCoefficient',
        align: 'center',
        render(value: number | null) {
          return <Text strong>{format2(value)}</Text>;
        },
      },
      {
        title: 'Appréciations',
        dataIndex: 'appreciation',
        key: 'appreciation',
        align: 'left',
        render(value: string | null) {
          return value || '';
        },
      },
    ],
    []
  );

  const statsColumns: ColumnsType<BulletinStatsRow> = useMemo(
    () =>
      coefficientMode
        ? [
            {
              title: 'Moyenne',
              dataIndex: 'moyenneEleve',
              key: 'moyenneEleve',
              align: 'center',
              render(value: number | null) {
                return value === null ? '-' : `${format2(value)}/${noteScale}`;
              },
            },
            {
              title: 'Moyenne de classe',
              dataIndex: 'moyenneClasse',
              key: 'moyenneClasse',
              align: 'center',
              render(value: number | null) {
                return value === null ? '-' : `${format2(value)}/${noteScale}`;
              },
            },
            { title: 'Mention', dataIndex: 'mention', key: 'mention', align: 'center' },
          ]
        : [
            {
              title: "Moyenne de l'élève",
              dataIndex: 'moyenneEleve',
              key: 'moyenneEleve',
              align: 'center',
              render(value: number | null) {
                return value === null ? '-' : `${format2(value)}/${noteScale}`;
              },
            },
            {
              title: 'Moyenne de la classe',
              dataIndex: 'moyenneClasse',
              key: 'moyenneClasse',
              align: 'center',
              render(value: number | null) {
                return value === null ? '-' : `${format2(value)}/${noteScale}`;
              },
            },
          ],
    [coefficientMode, noteScale]
  );

  const statsData: BulletinStatsRow[] = selectedBulletin
    ? [
        {
          key: selectedBulletin.inscriptionId,
          moyenneEleve: selectedBulletin.moyenneGenerale,
          moyenneClasse: selectedBulletin.moyenneClasse,
          mention: selectedBulletin.mention,
        },
      ]
    : [];

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      width={900}
      destroyOnClose
      title="Relevé de notes"
      footer={
        selectedBulletin ? (
          <Space>
            <Button onClick={onClose}>Fermer</Button>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              style={{ backgroundColor: KORI_ORANGE, borderColor: KORI_ORANGE }}
              onClick={() => onGeneratePdf(selectedBulletin)}
              disabled={!isPeriodeClosed}
            >
              Télécharger bulletin
            </Button>
          </Space>
        ) : (
          <Button onClick={onClose}>Fermer</Button>
        )
      }
    >
      {selectedBulletin ? (
        <div
          style={{
            background: 'linear-gradient(180deg, #fff8ef 0%, #ffffff 100%)',
            border: '1px solid #f6d9b5',
            borderRadius: 12,
            padding: 16,
          }}
        >
          <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                {`${selectedBulletin.prenom} ${selectedBulletin.nom}`}
              </Title>
              <Text type="secondary">{`Matricule: ${selectedBulletin.matricule || '-'}`}</Text>
            </Col>
            <Col>
              <Space wrap>
                <Tag color={KORI_ORANGE}>Rang: {selectedBulletin.rang ? `${selectedBulletin.rang}/${bulletinsCount}` : '-'}</Tag>
              </Space>
            </Col>
          </Row>

          {coefficientMode ? (
            <Table<MatiereBulletin>
              rowKey={(row, index) => `${selectedBulletin.inscriptionId}-${row.matiereId}-${index}`}
              columns={secondaryColumns}
              dataSource={coefficientRows}
              pagination={false}
              size="small"
              summary={() => (
                <Table.Summary>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>
                      <Text strong>Total</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} />
                    <Table.Summary.Cell index={2} />
                    <Table.Summary.Cell index={3}>
                      <Text strong style={{ display: 'block', textAlign: 'center' }}>
                        {selectedBulletin.moyenneGenerale === null ? '-' : `${format2(selectedBulletin.moyenneGenerale)}/20`}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>
                      <Text strong style={{ display: 'block', textAlign: 'center' }}>
                        {selectedBulletin.totalCoefficients || '-'}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>
                      <Text strong style={{ display: 'block', textAlign: 'center' }}>
                        {format2(selectedBulletin.totalPoints)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} />
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          ) : (
            <Table<GroupedMatiereBulletin>
              rowKey={row => `${selectedBulletin.inscriptionId}-${row.matiereId}`}
              columns={releveColumns}
              dataSource={groupedMatieres}
              pagination={false}
              summary={
                coefficientMode
                  ? undefined
                  : () => (
                      <Table.Summary>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}>
                            <Text strong style={{ textAlign: 'left', display: 'block' }}>
                              Total
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1}>
                            <Text strong style={{ display: 'block', textAlign: 'center' }}>
                              {format2(selectedBulletin.totalPoints)}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2}>
                            <Text strong style={{ display: 'block', textAlign: 'center' }}>
                              {format2(selectedBulletin.totalBareme)}
                            </Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    )
              }
            />
          )}
          <Table<BulletinStatsRow>
            style={{ marginTop: 12 }}
            rowKey="key"
            columns={statsColumns}
            dataSource={statsData}
            pagination={false}
            size="small"
          />
        </div>
      ) : (
        <Empty description="Aucun relevé sélectionné" />
      )}
    </Modal>
  );
};
